import type { WorkspaceTaskScope } from '../store/api/workspaceApi';

export const ROUTE_WIDE_DOCUMENT_TYPES = new Set(['CARRIER_INVOICE', 'LOADING_INVOICE']);

/** Route/operational types that belong in «Документы маршрута», not «Документы по заявке». */
export const ROUTE_OPERATIONAL_DOCUMENT_TYPES = new Set([
  'LOADING_PHOTO',
  'LOADING_TN',
  'UNLOADING_PHOTO',
  'UNLOADING_DOC',
  'CARRIER_INVOICE',
  'LOADING_INVOICE',
  'RAIL_WAYBILL',
  'AWB',
  'POWER_OF_ATTORNEY',
  'SCANS_SENT',
]);

export function isRouteOperationalDocumentType(documentType?: string | null): boolean {
  return ROUTE_OPERATIONAL_DOCUMENT_TYPES.has(String(documentType || '').trim().toUpperCase());
}

/**
 * Application block display filter (DOCUMENT-OWNERSHIP-DISPLAY-FIX-001).
 * Excludes route-bound operational docs; keeps application-only copies (routeId == null).
 */
export function filterApplicationDocumentsForDisplay<
  T extends { documentType?: string | null; routeId?: number | null },
>(documents: T[] | undefined): T[] {
  if (!documents?.length) return [];
  return documents.filter((doc) => {
    if (!isRouteOperationalDocumentType(doc.documentType)) return true;
    return doc.routeId == null;
  });
}

export type DocumentUploadScope = {
  applicationId?: number;
  groupId?: number;
  routeStopId?: number;
  taskScope?: WorkspaceTaskScope;
};

export function isRouteWideDocumentType(documentType?: string | null): boolean {
  return ROUTE_WIDE_DOCUMENT_TYPES.has(String(documentType || '').trim().toUpperCase());
}

/** Route docs visible under an application panel: route-wide (null app) or matching application. */
export function filterRouteDocumentsForApplication<T extends { applicationId?: number | null }>(
  documents: T[] | undefined,
  applicationId: number
): T[] {
  if (!documents?.length) return [];
  return documents.filter(
    (doc) => doc.applicationId == null || Number(doc.applicationId) === applicationId
  );
}

export function buildScopedUploadPayloadFields(params: {
  route?: { id?: number } | null;
  application?: { id?: number } | null;
  uploadScope?: DocumentUploadScope | null;
  documentType?: string;
}): {
  applicationId?: number;
  routeId?: number;
  groupId?: number;
  routeStopId?: number;
} {
  const routeId = params.route?.id != null ? Number(params.route.id) : undefined;

  let applicationId: number | undefined;
  if (params.uploadScope?.applicationId != null && Number.isFinite(Number(params.uploadScope.applicationId))) {
    applicationId = Number(params.uploadScope.applicationId);
  } else if (!params.route && params.application?.id != null) {
    applicationId = Number(params.application.id);
  } else if (
    params.route &&
    params.application?.id != null &&
    !isRouteWideDocumentType(params.documentType)
  ) {
    applicationId = Number(params.application.id);
  }

  const groupId =
    params.uploadScope?.groupId != null && Number.isFinite(Number(params.uploadScope.groupId))
      ? Number(params.uploadScope.groupId)
      : undefined;
  const routeStopId =
    params.uploadScope?.routeStopId != null && Number.isFinite(Number(params.uploadScope.routeStopId))
      ? Number(params.uploadScope.routeStopId)
      : undefined;

  return { applicationId, routeId, groupId, routeStopId };
}

export function buildRouteUploadScopeFromTask(task: {
  applicationId?: number | null;
  groupId?: number | null;
  routeStopId?: number | null;
  taskScope?: WorkspaceTaskScope | null;
}): DocumentUploadScope | undefined {
  const applicationId =
    task.applicationId != null && Number.isFinite(Number(task.applicationId))
      ? Number(task.applicationId)
      : undefined;
  const groupId =
    task.groupId != null && Number.isFinite(Number(task.groupId)) ? Number(task.groupId) : undefined;
  const routeStopId =
    task.routeStopId != null && Number.isFinite(Number(task.routeStopId))
      ? Number(task.routeStopId)
      : undefined;
  const taskScope = task.taskScope ?? undefined;

  if (applicationId == null && groupId == null && routeStopId == null) {
    return undefined;
  }

  return { applicationId, groupId, routeStopId, taskScope };
}
